import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { formatMoney } from '@theme/money-formatting';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * Whether a cart request is in flight, and the change waiting on it.
   *
   * Quantity changes used to sit behind a 300ms trailing debounce, so a single
   * press of "+" paid 300ms of dead time before its request even started. The
   * debounce was there to keep a flurry of presses from becoming a flurry of
   * requests, which this does instead: the first press goes out immediately,
   * and anything pressed while that is in flight collapses into one follow-up
   * sent when it lands. The stepper's own number updates locally on every
   * press either way, so the shopper sees each one.
   *
   * @type {boolean}
   */
  #requestInFlight = false;

  /** @type {ChildNode[] | null} Nodes replaced by the optimistic empty-cart state, kept to undo it on failure. */
  #emptiedFrom = null;

  /** @type {Promise<void> | null} */
  #emptyTransition = null;

  /** @type {{line: number, quantity: number} | null} */
  #queuedChange = null;

  /**
   * Which prices the last quantity change was able to work out for itself, so
   * the request that follows knows not to put a loading shimmer over a number
   * that is already correct on screen.
   *
   * @type {{line: boolean, total: boolean}}
   */
  #painted = { line: false, total: false };

  /**
   * Renders the new line price, and the new cart total, without waiting for
   * the server.
   *
   * A quantity change is multiplication, and the answer is already on the
   * page: each row carries its unit price, and Liquid has marked whether that
   * row's price is plain unit x quantity (see cart-products.liquid, which
   * refuses the mark for volume pricing, line-level discounts and
   * measurement-priced items). Where it is, the arithmetic here matches what
   * the server will send, so the morph that follows changes nothing visible.
   * Where it isn't, this paints nothing and the shimmer stays: a wrong number
   * shown confidently is worse than a slow one.
   *
   * @param {number} line - The line being changed.
   * @param {number} quantity - Its new quantity.
   * @returns {{line: boolean, total: boolean}} What got painted.
   */
  #paintPrices(line, quantity) {
    const painted = { line: false, total: false };
    const money = this.#moneyFormat();
    const row = this.refs.cartItemRows?.[line - 1];
    if (!money || !row) return painted;

    const unitPrice = Number(row.dataset.unitPrice);
    if (row.dataset.linearPrice === 'true' && Number.isFinite(unitPrice)) {
      painted.line = this.#setMoney(
        row.querySelector('.cart-items__price text-component'),
        unitPrice * quantity,
        money
      );
    }

    const { cartTotal } = this.refs;
    if (cartTotal?.dataset.linearTotal !== 'true') return painted;

    // Every line has to be linear for the sum to be, not just the one that
    // changed -- the total is all of them.
    let sum = 0;
    for (const [index, itemRow] of (this.refs.cartItemRows ?? []).entries()) {
      const rowUnitPrice = Number(itemRow.dataset.unitPrice);
      if (itemRow.dataset.linearPrice !== 'true' || !Number.isFinite(rowUnitPrice)) return painted;

      const rowQuantity =
        index === line - 1 ? quantity : Number(itemRow.querySelector('input[name="updates[]"]')?.value);
      if (!Number.isFinite(rowQuantity)) return painted;

      sum += rowUnitPrice * rowQuantity;
    }

    painted.total = this.#setMoney(cartTotal, sum, money);

    return painted;
  }

  /**
   * Writes a money value into a text-component, matching how the server
   * renders it: the visible text, and the `value` attribute the morph and the
   * payment-terms element read.
   *
   * @param {Element | null} element - The text-component.
   * @param {number} cents - The amount in the currency's minor units.
   * @param {{format: string, currency: string}} money - The store's format.
   * @returns {boolean} Whether it was written.
   */
  #setMoney(element, cents, money) {
    if (!(element instanceof HTMLElement)) return false;

    const formatted = formatMoney(cents, money.format, money.currency);
    element.textContent = formatted;
    element.setAttribute('value', formatted);

    return true;
  }

  /**
   * The store's money format and currency, as Liquid rendered them.
   * @returns {{format: string, currency: string} | null}
   */
  #moneyFormat() {
    const source = this.querySelector('[data-money-format][data-currency]');
    if (!(source instanceof HTMLElement)) return null;

    const { moneyFormat: format, currency } = source.dataset;
    if (!format || !currency) return null;

    return { format, currency };
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  /**
   * Sends a quantity change straight away, or coalesces it into the one
   * already in flight.
   *
   * Rapid presses on one line send absolute quantities, so keeping only the
   * latest is correct -- and a quantity change never reorders lines, so the
   * line number a queued change carries still points at the same line once
   * the in-flight response has morphed.
   *
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    // Before anything is sent, and whether or not a request is already in
    // flight: the press should land on screen now, not a round trip later.
    this.#painted = quantity > 0 ? this.#paintPrices(line, quantity) : { line: false, total: false };

    if (this.#requestInFlight) {
      this.#queuedChange = { line, quantity };
      return;
    }

    this.#onQuantityChange(event);
  };

  /**
   * Sends whatever was pressed while the last request was in flight.
   */
  #flushQueuedChange() {
    const queued = this.#queuedChange;
    if (!queued) return;

    this.#queuedChange = null;

    if (queued.quantity === 0) {
      this.onLineItemRemove(queued.line);
      return;
    }

    this.updateQuantity({ line: queued.line, quantity: queued.quantity, action: 'change' });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    if (this.#painted.line) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      const hadFocus = this.contains(document.activeElement);
      this.#emptiedFrom = [...this.childNodes];

      this.#emptyTransition = startViewTransition(() => {
        this.replaceChildren(clone);

        // The focused Remove button was just replaced; keep keyboard users inside the cart.
        if (hadFocus) {
          const next = this.querySelector('a[href], button:not([disabled])');
          if (next instanceof HTMLElement) next.focus();
        }
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    let failed = false;
    this.#requestInFlight = true;
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    if (!this.#painted.total) cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        let newCartItemCount;
        if (typeof parsedResponseText.item_count === 'number') {
          newCartItemCount = parsedResponseText.item_count;
        } else {
          // Fallback: read the count from the hidden element in the section HTML
          const newSectionHTML = new DOMParser().parseFromString(
            parsedResponseText.sections[this.sectionId],
            'text/html'
          );
          const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
          newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
        }

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], { mode: this.isDrawer ? 'hydration' : 'full' });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        failed = true;
        resetShimmer(this);
        // The optimistic empty state was wrong: put the lines back before showing the error on one.
        // The view transition runs its callback asynchronously: wait for it so the empty state can't overwrite the restore.
        const restoreAndReport = () => {
          if (this.#emptiedFrom) {
            this.replaceChildren(...this.#emptiedFrom);
            this.#enableCartItems();
          }
          this.#emptiedFrom = null;
          this.#emptyTransition = null;
          // Refs are re-collected by a mutation observer after the nodes go back in; report on the next task.
          setTimeout(() => {
            try {
              this.#handleCartError(line, {
                errors: Theme.translations.cart_update_error || 'Could not update your cart. Please try again.',
              });
            } catch (_) {
              // Error elements missing; nothing more to show.
            }
          }, 0);
        };
        if (this.#emptiedFrom && this.#emptyTransition) this.#emptyTransition.then(restoreAndReport);
        else restoreAndReport();
      })
      .finally(() => {
        if (!failed) {
          this.#emptiedFrom = null;
          this.#emptyTransition = null;
        }
        this.#requestInFlight = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
        this.#flushQueuedChange();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    // The prices were painted for the quantity the shopper asked for, and the
    // cart refused it. Put them back to what the reverted quantity comes to,
    // or nothing on screen would match the number in the stepper.
    const revertedQuantity = Number(quantityInput.value);
    if (Number.isFinite(revertedQuantity) && revertedQuantity > 0) {
      this.#painted = this.#paintPrices(line, revertedQuantity);
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
